/**
 * First-party analytics: which screens are opened and what is tapped. Events
 * are batched and sent to /api/track; the server adds who (from the session
 * cookie), where (Cloudflare's city/region) and the device. Nothing typed into
 * a field is ever recorded, only the label of what was tapped.
 */

type Event = { kind: 'open' | 'view' | 'click'; path: string; label?: string; ref?: string };

const queue: Event[] = [];
let timer: number | null = null;

function sessionId(): string {
  try {
    let id = sessionStorage.getItem('rb-session');
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem('rb-session', id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

function flush(useBeacon = false): void {
  if (!queue.length) return;
  const body = JSON.stringify({ session: sessionId(), events: queue.splice(0, 30) });
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon('/api/track', new Blob([body], { type: 'application/json' }));
  } else {
    void fetch('/api/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true, credentials: 'same-origin' }).catch(() => undefined);
  }
  if (queue.length) flush(useBeacon);
}

function push(e: Event): void {
  queue.push(e);
  if (timer == null) timer = window.setTimeout(() => ((timer = null), flush()), 4000);
}

/** What a tapped element says, kept short: an explicit data-track wins, then its label or text. */
function labelOf(el: Element): string | null {
  const explicit = el.getAttribute('data-track') || el.getAttribute('aria-label') || el.getAttribute('title');
  const text = explicit ?? (el as HTMLElement).innerText ?? el.textContent ?? '';
  const line = text.replace(/\s+/g, ' ').trim();
  return line ? line.slice(0, 80) : null;
}

/** Start tracking: one "open" per visit, every tap on a button or link. */
export function startTracking(): void {
  push({ kind: 'open', path: location.pathname, ref: document.referrer || undefined });
  document.addEventListener(
    'click',
    (e) => {
      const el = (e.target as Element | null)?.closest?.('button, a, [role="button"], [data-track], summary');
      if (!el) return;
      const label = labelOf(el);
      if (label) push({ kind: 'click', path: location.pathname, label });
    },
    true,
  );
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}

/** A screen was shown. */
export function trackView(path: string): void {
  push({ kind: 'view', path });
}
